import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { NFT } from '@/types';
import { api } from '@/services/api';
import { FiEdit2, FiTrash2, FiPlus, FiDollarSign, FiShare2 } from 'react-icons/fi';
import { Link } from 'react-router-dom';

const getUserNFTs = async (): Promise<NFT[]> => {
  const response = await api.get('/api/nfts/me');
  if (response && response.data && Array.isArray(response.data.data)) {
    return response.data.data;
  }
  return [];
};

const MyNFTsPage: React.FC = () => {
  const { data: nfts = [], isLoading, error } = useQuery<NFT[]>({
    queryKey: ['myNFTs'],
    queryFn: getUserNFTs,
  });

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-dark-800 rounded-lg p-4 h-48"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center text-red-500">
          Error loading NFTs. Please try again later.
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-white">My NFTs</h1>
        <Link
          to="/dashboard/nfts/create"
          className="btn-primary flex items-center space-x-2"
        >
          <FiPlus />
          <span>Mint New NFT</span>
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {nfts.map((nft: NFT) => (
          <div key={nft.id} className="card">
            <div className="aspect-square mb-4 relative group">
              <img
                src={nft.metadata?.image}
                alt={nft.metadata?.name}
                className="w-full h-full object-cover rounded-t-xl"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(nft.metadata?.name || 'NFT')}&background=7c3aed&color=fff`;
                }}
              />
              <div className="absolute inset-0 bg-dark-900/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-4">
                <button
                  className="btn-icon btn-primary"
                  title="List for Sale"
                  onClick={() => {
                    // TODO: Implement list for sale functionality
                  }}
                >
                  <FiDollarSign />
                </button>
                <button
                  className="btn-icon btn-outline"
                  title="Share NFT"
                  onClick={() => {
                    // TODO: Implement share functionality
                  }}
                >
                  <FiShare2 />
                </button>
              </div>
            </div>
            <div className="p-4">
              <h2 className="text-xl font-semibold text-white mb-2">{nft.metadata?.name}</h2>
              <p className="text-gray-400 mb-4 line-clamp-2">{nft.metadata?.description}</p>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">
                  Token ID: {nft.tokenId}
                </span>
                <div className="flex space-x-2">
                  <Link
                    to={`/dashboard/nfts/${nft.id}/edit`}
                    className="btn-icon btn-outline"
                    title="Edit NFT"
                  >
                    <FiEdit2 />
                  </Link>
                  <button
                    className="btn-icon btn-outline text-red-500 hover:text-red-600"
                    title="Transfer NFT"
                    onClick={() => {
                      // TODO: Implement transfer functionality
                    }}
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}

        {nfts.length === 0 && (
          <div className="col-span-full text-center py-12">
            <div className="text-gray-400 mb-4">You haven't minted any NFTs yet</div>
            <Link to="/dashboard/nfts/create" className="btn-primary">
              Mint Your First NFT
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyNFTsPage;
